import React, { useState } from 'react';
import { db } from '../../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { useAnalytics } from '../../hooks/useAnalytics';

const JoinNetwork = () => {
    const { logEvent, events } = useAnalytics();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        colonia: '',
        interest: '',
        message: new URLSearchParams(window.location.search).get('message') || ''
    });
    const [status, setStatus] = useState('idle'); // idle, loading, success, error

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');

        try {
            await addDoc(collection(db, "i3_leads"), {
                ...formData,
                createdAt: serverTimestamp(),
                source: 'landing_page'
            });

            logEvent(events.FORM_SUBMIT, {
                form_name: 'join_network',
                interest: formData.interest,
                colonia: formData.colonia
            });

            setStatus('success');
            setFormData({ name: '', email: '', phone: '', colonia: '', interest: '', message: '' });
        } catch (error) {
            console.error("Error writing document: ", error);
            setStatus('error');
            logEvent(events.ERROR, {
                component: 'JoinNetwork',
                error_message: error.message
            });
        }
    };

    return (
        <section id="unete" className="py-24 bg-background relative border-t border-zinc-800">
            <div className="container mx-auto px-6 max-w-5xl">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-display font-bold text-white mb-4">COLABORA CON i³</h2>
                    <p className="text-text-muted">Únete a la comunidad de técnicos y ciudadanos construyendo la visión 2030.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    {/* Roles Grid */}
                    <div className="grid grid-cols-1 gap-6">
                        <div className="bg-surface p-6 rounded-xl border border-zinc-800">
                            <h4 className="font-bold text-white mb-2">🎓 Voluntariado Técnico</h4>
                            <p className="text-sm text-zinc-500 mb-4">Estudiantes y pros para levantamiento de datos.</p>
                        </div>
                        <div className="bg-surface p-6 rounded-xl border border-zinc-800">
                            <h4 className="font-bold text-white mb-2">💼 Consejo Consultivo</h4>
                            <p className="text-sm text-zinc-500 mb-4">Expertos urbanistas para diseño de políticas.</p>
                        </div>
                        <div className="bg-surface p-6 rounded-xl border border-zinc-800">
                            <h4 className="font-bold text-white mb-2">🔬 Donante</h4>
                            <p className="text-sm text-zinc-500 mb-4">Financia estudios deducibles de impuestos (status como Donataria Autorizada en trámite).</p>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="bg-surface p-8 rounded-2xl border border-zinc-800 shadow-xl">
                        <h3 className="text-xl font-bold text-white mb-6">Regístrate</h3>

                        {status === 'success' ? (
                            <div className="bg-green-500/10 border border-green-500 text-green-500 p-4 rounded-lg flex items-center gap-3">
                                <CheckCircle size={24} />
                                <div>
                                    <p className="font-bold">¡Registro Exitoso!</p>
                                    <p className="text-sm">Te contactaremos pronto. Gracias por sumarte.</p>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <input
                                        name="name" value={formData.name} onChange={handleChange} required
                                        type="text" placeholder="Nombre"
                                        className="bg-background border border-zinc-700 text-white p-3 rounded focus:border-primary focus:outline-none text-sm w-full"
                                    />
                                    <input
                                        name="email" value={formData.email} onChange={handleChange} required
                                        type="email" placeholder="Email"
                                        className="bg-background border border-zinc-700 text-white p-3 rounded focus:border-primary focus:outline-none text-sm w-full"
                                    />
                                </div>
                                <input
                                    name="phone" value={formData.phone} onChange={handleChange} required
                                    type="tel" placeholder="WhatsApp"
                                    className="w-full bg-background border border-zinc-700 text-white p-3 rounded focus:border-primary focus:outline-none text-sm"
                                />

                                <div className="grid grid-cols-2 gap-4">
                                    <select
                                        name="colonia" value={formData.colonia} onChange={handleChange} required
                                        className="bg-background border border-zinc-700 text-zinc-400 p-3 rounded focus:border-primary focus:outline-none text-sm w-full"
                                    >
                                        <option value="">Colonia...</option>
                                        <option value="Agricola Oriental">Agrícola Oriental</option>
                                        <option value="Pantitlan">Pantitlán</option>
                                        <option value="Granjas Mexico">Granjas México</option>
                                        <option value="Ramos Millan">Ramos Millán</option>
                                        <option value="Santa Anita">Santa Anita</option>
                                        <option value="Otra">Otra</option>
                                    </select>
                                    <select
                                        name="interest" value={formData.interest} onChange={handleChange} required
                                        className="bg-background border border-zinc-700 text-zinc-400 p-3 rounded focus:border-primary focus:outline-none text-sm w-full"
                                    >
                                        <option value="">Interés...</option>
                                        <option value="Voluntario">Voluntariado</option>
                                        <option value="Simpatizante">Simpatizante</option>
                                        <option value="Consejo">Consejo Consultivo</option>
                                        <option value="Donante">Donante</option>
                                    </select>
                                </div>

                                <textarea
                                    name="message" value={formData.message} onChange={handleChange}
                                    rows="3" placeholder="Mensaje opcional..."
                                    className="w-full bg-background border border-zinc-700 text-white p-3 rounded focus:border-primary focus:outline-none text-sm"
                                ></textarea>

                                <button
                                    type="submit"
                                    disabled={status === 'loading'}
                                    className="w-full bg-primary text-black font-bold py-3 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                                >
                                    {status === 'loading' ? 'ENVIANDO...' : 'ENVIAR DATOS'}
                                </button>

                                {status === 'error' && (
                                    <p className="text-red-500 text-xs flex items-center gap-1 mt-2">
                                        <AlertCircle size={14} /> Error al enviar. Intenta de nuevo.
                                    </p>
                                )}
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default JoinNetwork;
